import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
  Autocomplete,
  Chip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { subDays, differenceInCalendarDays, isSameDay } from 'date-fns';
import { useAgentStore } from '../../stores/agentStore';
import { ALL_SURFACES, surfaceColor, surfaceLabel } from '../../utils/agentSurfaces';
import { formatOrgDisplayName } from '../../utils/display';
import type { FilterOption } from '../../types';

interface AgentFilterBarProps {
  showSearch?: boolean;
}

const QUICK_RANGES = [
  { label: 'Today', days: 1 },
  { label: '7D', days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
];

export default function AgentFilterBar({ showSearch = false }: AgentFilterBarProps) {
  const {
    dateRange,
    surfaces,
    search,
    orgIds,
    userIds,
    availableOrgs,
    availableUsers,
    setDateRange,
    setSurfaces,
    setSearch,
    setOrgIds,
    setUserIds,
    loadFilterOptions,
  } = useAgentStore();
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const applyQuickRange = (days: number) => {
    const end = new Date();
    setDateRange(subDays(end, days - 1), end);
  };
  const isRangeActive = (days: number) =>
    isSameDay(dateRange.end, new Date()) &&
    differenceInCalendarDays(dateRange.end, dateRange.start) === days - 1;

  const selectedOrgs = availableOrgs.filter((o) => orgIds.includes(o.id));
  const selectedUsers = availableUsers.filter((u) => userIds.includes(u.id));

  return (
    <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      <ButtonGroup size="small" variant="outlined">
        {QUICK_RANGES.map((r) => (
          <Button
            key={r.label}
            onClick={() => applyQuickRange(r.days)}
            variant={isRangeActive(r.days) ? 'contained' : 'outlined'}
            sx={{ minWidth: 44, px: 1, fontSize: '0.72rem' }}
          >
            {r.label}
          </Button>
        ))}
      </ButtonGroup>

      <DatePicker
        label="Start"
        value={dateRange.start}
        onChange={(d) => d && setDateRange(d, dateRange.end)}
        slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
      />
      <DatePicker
        label="End"
        value={dateRange.end}
        onChange={(d) => d && setDateRange(dateRange.start, d)}
        slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
      />

      <Autocomplete
        multiple
        size="small"
        options={availableOrgs}
        getOptionLabel={(o: FilterOption) => formatOrgDisplayName(o.name)}
        value={selectedOrgs}
        onChange={(_, v) => setOrgIds(v.map((o) => o.id))}
        renderInput={(params) => <TextField {...params} label="Org" placeholder="All" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={formatOrgDisplayName(option.name)}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          ))
        }
        sx={{ minWidth: 180 }}
      />

      <Autocomplete
        multiple
        size="small"
        options={availableUsers}
        getOptionLabel={(u: FilterOption) => u.name}
        value={selectedUsers}
        onChange={(_, v) => setUserIds(v.map((u) => u.id))}
        renderInput={(params) => <TextField {...params} label="User" placeholder="All" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.name}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          ))
        }
        sx={{ minWidth: 180 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Surface:
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={surfaces}
          onChange={(_, next) => setSurfaces(next as string[])}
          sx={{ flexWrap: 'wrap' }}
        >
          {ALL_SURFACES.map((s) => (
            <ToggleButton
              key={s}
              value={s}
              sx={{
                textTransform: 'none',
                px: 1.25,
                py: 0.25,
                borderColor: 'rgba(148,163,184,0.2)',
                '&.Mui-selected': {
                  backgroundColor: `${surfaceColor(s)}22`,
                  color: surfaceColor(s),
                  borderColor: surfaceColor(s),
                  '&:hover': { backgroundColor: `${surfaceColor(s)}33` },
                },
              }}
            >
              {surfaceLabel(s)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {showSearch && (
        <TextField
          size="small"
          placeholder="Search asks..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput);
          }}
          onBlur={() => setSearch(searchInput)}
          sx={{ width: 240, ml: 'auto' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
    </Paper>
  );
}
